import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { SelectItem } from 'primeng/api';
import { Calendar } from 'primeng/calendar';
import { MultiSelect } from 'primeng/multiselect';
import { ReplaySubject, Subject } from 'rxjs';
import { debounceTime, filter, takeUntil } from 'rxjs/operators';

import { DateUtils, translationKeys } from '../../../../core';
import { ScipTableFilterService } from './scip-table-filter.service';
import { TableColumnFilterConfiguration, TableColumnFilterMode } from '@avano/avano-components/avano-table';

@Component({
  selector: 'scip-table-filter',
  templateUrl: './scip-table-filter.component.html',
  styleUrls: ['./scip-table-filter.component.scss']
})
export class ScipTableFilterComponent implements OnInit, OnDestroy {
  static AVANO_USER_INPUT_DELAY_TIME_IN_MS = 300;
  static AVANO_USER_INPUT_MIN_LENGTH = 2;

  @Input() set filterConfiguration(filterConfiguration: TableColumnFilterConfiguration) {
    // Developer placed a scip-table-filter, but without a dedicated configuration.
    // This means he wants a plain input-text-filter, nothing special.
    this.filterMode = TableColumnFilterMode.PLAINTEXT;
    if (filterConfiguration) {
      this.handleConfigurationFromOutside(filterConfiguration);
    }
  }

  // Identifier in which parent table our instance is placed, later used for resetting all filters within one table at once!
  @Input() parentTableIdentifier: string;

  @Input() restoredValue: any;

  @Output() readonly userInput = new EventEmitter<unknown>();

  @ViewChild('scipFilter')
  private primeNgComponentRef: MultiSelect | Calendar;

  filterMode: TableColumnFilterMode;

  // Holds the current value for input, single selection and calendar
  currentUserInput: any;

  //Singleselection flag/label field;
  isSingleSelectLabelField: any;
  singleSelectLabelField: string;

  // Singleselection/Multiselection
  valuesLabelField: string; // If we have objects, that is the property which is shown to the user e.g. name of org (org.name)
  filterValues: any[] = [];

  // Multiselection and Singleselection
  // Both use a multiselect primeng component
  filterValuesSuggestions: SelectItem[] = [];

  // Used within the html to distinguish between the different filtermodes (switchcase)
  allFilterModes: typeof TableColumnFilterMode = TableColumnFilterMode;

  // For the calendar date picker
  todayDate: Date;

  translationKeys: typeof translationKeys = translationKeys;

  // For a plain textinput we do not want to query the backend on every user keystroke, only if he "stops" typing
  private userInputThrottled$ = new Subject<unknown>();
  private destroyed$: Subject<boolean> = new ReplaySubject(1);

  constructor(private scipTableFilterService: ScipTableFilterService) {}

  @HostListener('click', ['$event'])
  private handleClick(event: Event) {
    // Our filters are used in the table column header. Clicking there triggers the sorting on that column, therefore we
    // should prevent the bubbling of our click. If the user clicks on the filter, we do NOT want to trigger the sorting.
    event.stopPropagation();

    // But we want other opened overlays to be close, so lets close them.
    this.scipTableFilterService.filterClicked(this);
    return false;
  }

  ngOnInit(): void {
    if (this.filterMode === TableColumnFilterMode.PLAINTEXT) {
      this.userInputThrottled$
        .pipe(
          filter(() => this.currentUserInput?.length >= ScipTableFilterComponent.AVANO_USER_INPUT_MIN_LENGTH || !this.currentUserInput?.length),
          debounceTime(ScipTableFilterComponent.AVANO_USER_INPUT_DELAY_TIME_IN_MS),
          takeUntil(this.destroyed$)
        )
        .subscribe(() => {
          this.broadcastUserInput();
        });
    }

    if (this.parentTableIdentifier) {
      this.scipTableFilterService.register(this.parentTableIdentifier, this);
    }

    this.restorePersistedFilter();
    this.todayDate = new Date();
  }

  ngOnDestroy(): void {
    if (this.parentTableIdentifier) {
      this.scipTableFilterService.unregister(this.parentTableIdentifier, this);
    }
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  broadcastUserInput(event?: any): void {
    if (this.filterMode === TableColumnFilterMode.DATEPICKER_SINGLE) {
      this.userInput.emit(DateUtils.convertDateForBackend(this.currentUserInput));
    } else if (this.filterMode === TableColumnFilterMode.AUTOCOMPLETE_SINGLE) {
      // we only want to select one item at once so automatically unselect the old one if there is one
      if (this.currentUserInput.length) {
        this.currentUserInput = [event.itemValue];
      }
      this.userInput.emit(this.currentUserInput[0]);
      if (this.isSingleSelectLabelField) {
        let value: string[] = this.filterValues
          .filter((singleItem) => {
            return singleItem.value === this.currentUserInput[0];
          })
          .map((filterItem) => {
            return filterItem.label;
          });
        if (value.length > 0) {
          this.singleSelectLabelField = value[0];
        }
      }
    } else {
      this.userInput.emit(this.currentUserInput);
    }
  }

  clearUserInput(): void {
    this.resetFilter();
    // If the clearing of the filter is triggered for only one single field, we want to emit this change
    this.userInput.emit(this.currentUserInput);
  }

  throttleUserInput(): void {
    this.userInputThrottled$.next();
  }

  outsideClickDetected(): void {
    switch (this.filterMode) {
      case TableColumnFilterMode.AUTOCOMPLETE_MULTIPLE:
      case TableColumnFilterMode.AUTOCOMPLETE_SINGLE:
        if ((this.primeNgComponentRef as MultiSelect).overlayVisible) {
          (this.primeNgComponentRef as MultiSelect).hide();
        }
        break;
      case TableColumnFilterMode.DATEPICKER_SINGLE:
        if ((this.primeNgComponentRef as Calendar).overlayVisible) {
          (this.primeNgComponentRef as Calendar).hideOverlay();
        }
        break;
    }
  }

  resetFilter(): void {
    if (this.filterMode === TableColumnFilterMode.AUTOCOMPLETE_MULTIPLE) {
      this.currentUserInput = [];
    } else {
      this.currentUserInput = undefined;
    }
  }

  private handleConfigurationFromOutside(filterConfiguration: TableColumnFilterConfiguration) {
    if (filterConfiguration.filterMode) {
      this.filterMode = filterConfiguration.filterMode;
    }

    if (this.filterMode === TableColumnFilterMode.AUTOCOMPLETE_SINGLE || this.filterMode === TableColumnFilterMode.AUTOCOMPLETE_MULTIPLE) {
      this.prepareAutoComplete(filterConfiguration);
    }
  }

  private prepareAutoComplete(filterConfiguration: TableColumnFilterConfiguration) {
    // PrimeNG "removed" the need for the Multiselect-Item-Structure, but as we convert currently
    // all to that structure we "hardcode" the "label/value" in the template.
    if (Array.isArray(filterConfiguration.values)) {
      // No matter if the user of this component is putting an array of strings or an array of objects,
      // we will just use them and if it's an array of objects, he hopefully uses the SelectItem-structure or at
      // least sets the valuesLabelField so that we set the visible label correctly
      this.filterValues = filterConfiguration.values;
      this.valuesLabelField = filterConfiguration.valuesLabel;
    } else if (typeof filterConfiguration.values === 'object') {
      // The developer just put an enum as values, lets just use them.
      // But we need to convert the values to an array we can work with.
      this.filterValues = Object.values(filterConfiguration.values);
    }

    if (filterConfiguration.isSingleSelectLabelField) {
      this.isSingleSelectLabelField = filterConfiguration.isSingleSelectLabelField;
    }

    // Initialize our suggestions with the same set of values
    this.filterValuesSuggestions = this.filterValues.map((singleItem) => {
      let labelToShow: string;
      if (singleItem.label && !this.valuesLabelField) {
        labelToShow = singleItem.label;
      } else if (this.valuesLabelField) {
        labelToShow = singleItem[this.valuesLabelField];
      } else {
        labelToShow = singleItem;
      }

      let valueToEmit: any;
      if (singleItem.value) {
        valueToEmit = singleItem.value;
      } else {
        valueToEmit = singleItem;
      }
      return { label: labelToShow, value: valueToEmit };
    });
  }

  private restorePersistedFilter() {
    if (this.restoredValue) {
      switch (this.filterMode) {
        case TableColumnFilterMode.PLAINTEXT:
          this.currentUserInput = this.restoredValue;
          break;
        case TableColumnFilterMode.DATEPICKER_SINGLE:
          this.currentUserInput = DateUtils.convertBackendDateToDate(this.restoredValue);
          break;
        case TableColumnFilterMode.AUTOCOMPLETE_SINGLE:
          if (this.valuesLabelField) {
            this.currentUserInput = [
              this.filterValues.find((singleValue) => {
                const searchString = '"' + this.restoredValue + '"';
                return JSON.stringify(singleValue).includes(searchString);
              })
            ];
          } else if (this.isSingleSelectLabelField) {
            let value: string[] = this.filterValues
              .filter((singleItem) => {
                return singleItem.value === this.restoredValue;
              })
              .map((filterItem) => {
                return filterItem.label;
              });
            if (value.length > 0) {
              this.singleSelectLabelField = value[0];
              this.currentUserInput = [this.restoredValue];
            }
          } else {
            this.currentUserInput = [this.restoredValue];
          }
          break;
        case TableColumnFilterMode.AUTOCOMPLETE_MULTIPLE:
          this.currentUserInput = [];
          this.restoredValue.split(',').forEach((singleRestoredValue) => {
            const foundInValues = this.filterValuesSuggestions.find((singleValue) => {
              const searchString = '"' + singleRestoredValue + '"';
              return JSON.stringify(singleValue).includes(searchString);
            });
            if (foundInValues) {
              this.currentUserInput.push(foundInValues.value);
            }
          });
          break;
      }
    }
  }
}
